import { FC, useCallback, useMemo, useState } from 'react';

import { Grid } from '@mui/material';
import type { GetPartnerResources, Resource } from 'types/graphql';

import { MetaTags, useQuery } from '@redwoodjs/web';

import FilterSection from 'src/components/CustomFilter/FilterSection';
import Loader from 'src/components/Loader/Loader';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast } from 'src/components/Toasts/toast';
import { RESOURCE_TABLE_HEADERS } from 'src/constants';
import { GET_PARTNER_RESOURCES_QUERY } from 'src/graphql/queries/partnerResources.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { LogError } from 'src/lib/error-logger';
import {
    filteredPartnerResourcesTableData,
    partnerResourcesFilterOptions,
    updateTableFilters,
} from 'src/utils/tableUtils';

import { ViewResourceModal } from './components';
import SectionHeader from './components/SectionHeader';

const ViewPartnerResourcesPage: FC = () => {
    interface TableFilters {
        section?: string;
        resourceName?: string;
    }

    const [tableFilters, setTableFilters] = useState<TableFilters>({});
    const [open, setOpenModal] = useState(false);
    const [selectedResource, setSelectedResource] = useState(null);
    const [showEditResourceModal, setShowEditResourceModal] = useState(false);

    const {
        data,
        loading,
        refetch: refetchLatestResources,
    } = useQuery<GetPartnerResources>(GET_PARTNER_RESOURCES_QUERY, {
        fetchPolicy: 'network-only',
        onError: (error) => {
            showErrorToast('There was an error retrieving the resources');
            LogError(error);
        },
    });

    const partnerResources = data?.getPartnerResources || [];

    const filterOptions = useMemo(() => {
        if (!data || !partnerResources) return [];
        return partnerResourcesFilterOptions(data);
    }, [data]);

    const tableData = useMemo(() => {
        if (!data || !partnerResources) return [];
        const filteredData = filteredPartnerResourcesTableData(data, tableFilters);

        if (filteredData.length === 0) {
            return partnerResources?.flatMap((section) => {
                return section.resources;
            });
        }

        return filteredData;
    }, [data, tableFilters, partnerResources]);

    const filteredSections = useMemo(() => {
        if (!data || !partnerResources) return [];
        return partnerResources.filter((section) => {
            return tableData.some((resource) => {
                return resource.section.id === section.id;
            });
        });
    }, [data, tableData]);

    const handleFilterChange = useCallback((filter: string, newFilterValue: string) => {
        setTableFilters((prevFilters) => {
            return updateTableFilters(prevFilters, newFilterValue, filter);
        });
    }, []);

    const handleEditResourceModal = () => {
        setShowEditResourceModal(!showEditResourceModal);
    };

    const handleCloseViewModal = () => {
        setOpenModal(false);
    };

    const updatePartnerResourceAuditLog = (id: number) => {
        refetchLatestResources({
            id,
        });
    };

    const handleOpenViewResourceModal = (value: Resource) => {
        updatePartnerResourceAuditLog(value.id);
        setSelectedResource(value);
        setOpenModal(true);
    };

    return (
        <PageContent heading="Partner Resources" subHeading="View all partner resources">
            <MetaTags title="Partner Resources" description="View Partner Resources Page" />

            <Grid container sx={{ gap: '24px' }}>
                {/* Filter Section */}
                <Grid item xs={12}>
                    <FilterSection
                        filterOptions={filterOptions}
                        updateTableFilters={handleFilterChange}
                    />
                </Grid>

                {loading ? (
                    <Loader />
                ) : filteredSections.length > 0 ? (
                    filteredSections.map((section) => {
                        return (
                            <Grid item xs={12} key={section.id}>
                                <SectionHeader title={section.name} />
                                <TableList
                                    data={tableData.filter((res) => {
                                        return res.section.id === section.id;
                                    })}
                                    headers={RESOURCE_TABLE_HEADERS}
                                    handleViewRecord={(value: Resource) => {
                                        return handleOpenViewResourceModal(value);
                                    }}
                                    enableDelete={false}
                                    updateTableFilters={(
                                        filter: string,
                                        newFilterValue: string
                                    ) => {
                                        return setTableFilters((prevFilters) => {
                                            return updateTableFilters(
                                                prevFilters,
                                                newFilterValue,
                                                filter
                                            );
                                        });
                                    }}
                                />
                            </Grid>
                        );
                    })
                ) : null}
            </Grid>
            <ViewResourceModal
                open={open}
                handleCloseModal={handleCloseViewModal}
                resource={selectedResource}
                partnerResourceSections={partnerResources}
                refetchLatestResources={refetchLatestResources}
                handleEditResourceModal={handleEditResourceModal}
                showEditResourceModal={showEditResourceModal}
            />
        </PageContent>
    );
};

export default ViewPartnerResourcesPage;
